/**
 * Shopify Storefront Price Sync
 * Keeps product-card prices consistent with the PDP across markets.
 *
 * Two-step flow, shared by collections / search (Algolia), bridge sliders and
 * recommendation sections:
 *
 *  1. Entry-variant alignment (cards with a `handle` attribute)
 *     Re-fetches `/products/<handle>.js` (locale-aware, session-market prices)
 *     and aligns the card to the same entry variant as the PDP:
 *       - cheapest available when __OBLIST_VARIANT_DEFAULT__.enabled (default)
 *       - first available when the setting is rolled back
 *     Multi-price products get a localized “From / À partir de” prefix.
 *     B2B Trade Price overlays are never overwritten.
 *
 *  2. Country-context sync (cards without a handle, e.g. sold-out alternatives)
 *     Prices fetched from the Storefront API with @inContext(country) using the
 *     card's variant ID.
 *
 * ROLLBACK: Theme Settings → Product card → uncheck
 * “Default to cheapest available variant”, or set
 * window.__OBLIST_VARIANT_DEFAULT__.enabled = false
 */
(function () {
  'use strict';

  const STOREFRONT_API_VERSION = '2024-01';
  const BATCH_SIZE = 50;
  const PRICE_CACHE = new Map();   // variantId/handle -> { amount, currencyCode, priceVaries }
  const PRODUCT_CACHE = new Map(); // handle -> Promise<product.js payload | null>
  const DEBOUNCE_MS = 500;
  let debounceTimer = null;

  function getVariantDefaultConfig() {
    return window.__OBLIST_VARIANT_DEFAULT__ || { enabled: true, fromPrefix: 'From' };
  }

  function isCheapestDefaultEnabled() {
    return getVariantDefaultConfig().enabled !== false;
  }

  function getConfig() {
    return window.__SHOPIFY_STOREFRONT__ || {};
  }

  function getShopifyRoot() {
    try {
      if (window.Shopify && window.Shopify.routes && typeof window.Shopify.routes.root === 'string') {
        return window.Shopify.routes.root;
      }
    } catch (e) { /* ignore */ }
    return '/';
  }

  function getStorefrontUrl() {
    return `${getShopifyRoot()}api/${STOREFRONT_API_VERSION}/graphql.json`;
  }

  function formatPrice(amount, currencyCode) {
    const price = Math.round(parseFloat(amount));
    switch (currencyCode) {
      case 'USD': return `$${price}`;
      case 'GBP': return `£${price}`;
      case 'ILS': return `₪${price}`;
      case 'RUB': return `₽${price}`;
      default: return `${price}€`;
    }
  }

  function hasTradePriceOverlay(salePriceEl) {
    const text = (salePriceEl.textContent || '').toLowerCase();
    return text.indexOf('trade price') !== -1 || text.indexOf('prix trade') !== -1;
  }

  function getPurchasablePool(product) {
    if (!product || !product.variants || !product.variants.length) return [];
    const available = product.variants.filter((v) => v.available);
    return available.length ? available : product.variants;
  }

  function getEntryVariant(product) {
    const pool = getPurchasablePool(product);
    if (!pool.length) return null;
    if (isCheapestDefaultEnabled()) {
      return pool.reduce((min, v) => (v.price < min.price ? v : min));
    }
    // Legacy rollback: first available (Shopify selected_or_first_available)
    return pool.find((v) => v.available) || pool[0];
  }

  function priceVariesInPool(product) {
    const pool = getPurchasablePool(product);
    if (pool.length <= 1) return false;
    let min = pool[0].price;
    let max = pool[0].price;
    for (let i = 1; i < pool.length; i++) {
      if (pool[i].price < min) min = pool[i].price;
      if (pool[i].price > max) max = pool[i].price;
    }
    return min !== max;
  }

  function formatCardPrice(amount, currencyCode, priceVaries) {
    const formatted = formatPrice(amount, currencyCode);
    if (!priceVaries || !isCheapestDefaultEnabled()) return formatted;
    const prefix = getVariantDefaultConfig().fromPrefix || 'From';
    return `${prefix} ${formatted}`;
  }

  function setCardPrice(card, formatted) {
    const salePriceEl = card.querySelector('sale-price');
    if (!salePriceEl) return false;
    // Never clobber B2B Trade Price overlays injected by calTradingPrice
    if (hasTradePriceOverlay(salePriceEl)) {
      salePriceEl.classList.remove('price--loading');
      return false;
    }
    // Compare against visible text only (ignore sr-only "Price"/"Prix")
    const srOnly = salePriceEl.querySelector('.sr-only');
    let currentText = salePriceEl.textContent || '';
    if (srOnly && srOnly.textContent) {
      currentText = currentText.replace(srOnly.textContent, '');
    }
    currentText = currentText.replace(/\s+/g, ' ').trim();
    if (currentText === formatted) {
      salePriceEl.classList.remove('price--loading');
      return false;
    }
    salePriceEl.textContent = '';
    if (srOnly) salePriceEl.appendChild(srOnly);
    salePriceEl.appendChild(document.createTextNode(formatted));
    salePriceEl.classList.remove('price--loading');
    return true;
  }

  /* ----------------------------------------------------------------------
   * Step 1 — align cards to the PDP entry variant (per handle).
   * The AJAX endpoint resolves localized handles (FR pages) and returns
   * prices in the active session market/currency.
   * -------------------------------------------------------------------- */
  function fetchProduct(handle) {
    if (PRODUCT_CACHE.has(handle)) return PRODUCT_CACHE.get(handle);
    const promise = fetch(`${getShopifyRoot()}products/${encodeURIComponent(handle)}.js`)
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
    PRODUCT_CACHE.set(handle, promise);
    return promise;
  }

  async function alignCardsToEntryVariant(cards) {
    let aligned = 0;
    await Promise.all(cards.map(async (card) => {
      const handle = card.getAttribute('handle');
      if (!handle) return;
      const product = await fetchProduct(handle);
      if (!product || !product.variants || !product.variants.length) return;

      const entryVariant = getEntryVariant(product);
      if (!entryVariant) return;

      var currencyCode = 'EUR';
      try {
        if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
          currencyCode = window.Shopify.currency.active;
        }
      } catch (e) { /* ignore */ }
      const varies = priceVariesInPool(product);
      const priceData = {
        amount: Number(entryVariant.price) / 100,
        currencyCode: currencyCode,
        priceVaries: varies,
      };

      card.setAttribute('data-variant-id', String(entryVariant.id));
      card.setAttribute('data-price-varies', varies ? 'true' : 'false');
      PRICE_CACHE.set(String(entryVariant.id), priceData);
      PRICE_CACHE.set(handle, priceData);

      if (setCardPrice(card, formatCardPrice(priceData.amount, priceData.currencyCode, varies))) aligned++;
    }));
    if (aligned > 0) {
      console.log(`[Storefront Prices] Aligned ${aligned} cards to entry variant (${isCheapestDefaultEnabled() ? 'cheapest' : 'first'} available)`);
    }
  }

  /* ----------------------------------------------------------------------
   * Step 2 — country-context sync by variant ID (cards without a handle).
   * -------------------------------------------------------------------- */
  function buildNodesQuery(variantIds) {
    const country = getConfig().country || 'FR';
    const gids = variantIds.map(id => `"gid://shopify/ProductVariant/${id}"`).join(', ');
    return `query @inContext(country: ${country}) { nodes(ids: [${gids}]) { ... on ProductVariant { id price { amount currencyCode } product { handle } } } }`;
  }

  async function fetchBatch(variantIds) {
    const cfg = getConfig();
    if (!cfg.token) return {};

    const query = buildNodesQuery(variantIds);
    try {
      const response = await fetch(getStorefrontUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.token,
        },
        body: JSON.stringify({ query }),
      });
      if (!response.ok) return {};
      const data = await response.json();
      if (!data.data || !data.data.nodes) return {};

      const prices = {};
      data.data.nodes.forEach(node => {
        if (!node || !node.price || !node.product) return;
        const variantId = node.id.replace('gid://shopify/ProductVariant/', '');
        const handle = node.product.handle;
        const priceData = { amount: node.price.amount, currencyCode: node.price.currencyCode };
        prices[variantId] = priceData;
        prices[handle] = priceData;
        PRICE_CACHE.set(variantId, priceData);
        PRICE_CACHE.set(handle, priceData);
      });
      return prices;
    } catch (e) {
      console.warn('[Storefront Prices] Fetch error:', e);
      return {};
    }
  }

  function updateDOMPrices(prices) {
    let updated = 0;
    document.querySelectorAll('product-card[handle], .bridge-card[handle], .sold-out-alternatives__card[data-variant-id], #similar-pieces-seed[handle]').forEach(card => {
      const handle = card.getAttribute('handle');
      const variantId = card.getAttribute('data-variant-id');
      const priceData = (variantId && prices[variantId]) || prices[handle] || PRICE_CACHE.get(variantId) || PRICE_CACHE.get(handle);
      if (!priceData) return;
      const varies = priceData.priceVaries === true || card.getAttribute('data-price-varies') === 'true';
      if (setCardPrice(card, formatCardPrice(priceData.amount, priceData.currencyCode, varies))) updated++;
    });
    if (updated > 0) console.log(`[Storefront Prices] Updated ${updated} product prices`);
  }

  async function syncPrices() {
    try {
      const allCards = Array.prototype.slice.call(
        document.querySelectorAll('product-card[handle], .bridge-card[handle], .sold-out-alternatives__card[data-variant-id], #similar-pieces-seed[handle]')
      );
      if (allCards.length === 0) return;

      await alignCardsToEntryVariant(allCards.filter(function (card) {
        return card.getAttribute('handle');
      }));

      const variantIds = [];
      allCards.forEach(function (card) {
        const variantId = card.getAttribute('data-variant-id');
        if (variantId && !PRICE_CACHE.has(variantId)) variantIds.push(variantId);
      });

      if (variantIds.length === 0) {
        updateDOMPrices({});
        return;
      }

      let allPrices = {};
      for (let i = 0; i < variantIds.length; i += BATCH_SIZE) {
        const batch = variantIds.slice(i, i + BATCH_SIZE);
        const fetched = await fetchBatch(batch);
        Object.assign(allPrices, fetched);
      }

      updateDOMPrices(allPrices);
    } catch (e) {
      // Never leave prices invisible (Safari/Firefox/network failures).
      console.warn('[Storefront Prices] Sync error:', e);
    } finally {
      revealAllPrices();
    }
  }

  function revealAllPrices() {
    const nodes = document.querySelectorAll('sale-price.price--loading');
    for (let i = 0; i < nodes.length; i++) {
      nodes[i].classList.remove('price--loading');
    }
  }

  function syncVisible() {
    const fallbackTimer = setTimeout(revealAllPrices, 3000);
    const done = syncPrices();
    // Avoid Promise.finally for broader Safari/old-WebView safety; always reveal.
    if (done && typeof done.then === 'function') {
      done.then(function () { clearTimeout(fallbackTimer); }, function () { clearTimeout(fallbackTimer); });
    } else {
      clearTimeout(fallbackTimer);
    }
  }

  function debouncedSync() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { syncPrices(); }, DEBOUNCE_MS);
  }

  window.ShopifyPriceSync = {
    sync: syncPrices,
    syncVisible: syncVisible,
    debouncedSync: debouncedSync,
    formatPrice: formatPrice,
    getCache: () => PRICE_CACHE,
  };
})();
